import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Plus, Minus, Package, Video } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import Header from './Header';
import Footer from './Footer';

const KitConfigurator = () => {
  const { t, language, setLanguage } = useLanguage();

  // Products with prices
  const products = [
    { id: 'hub', nameKey: 'smartHub', price: 55.24 },
    { id: 'sensor', nameKey: 'sensor', price: 29.98 },
    { id: 'remote', nameKey: 'remote', price: 27.90 },
    { id: 'bulb', nameKey: 'smartBulbConfig', price: 12.00 },
    { id: 'plug', nameKey: 'smartPlugConfig', price: 12.50 },
  ];

  // Existing kits
  const kits = [
    { nameKey: 'starterKit', price: 289.99 },
    { nameKey: 'intermediateKit', price: 519.99 },
    { nameKey: 'completeKit', price: 659.99 },
  ];

  // Installation options
  const installationOptions = [
    { id: 'basic', nameKey: 'basicInstallation', price: 99.99 },
    { id: 'full', nameKey: 'fullInstallation', price: 199.99 },
  ];

  // State for quantities and installation
  const [quantities, setQuantities] = useState({
    hub: 0,
    sensor: 0,
    remote: 0,
    bulb: 0,
    plug: 0,
  });

  const [selectedInstallation, setSelectedInstallation] = useState(null);

  // Update quantity
  const updateQuantity = (productId, delta) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(0, prev[productId] + delta)
    }));
  };

  // Calculate total
  const calculateTotal = () => {
    let total = 0;
    
    // Add product prices
    products.forEach(product => {
      total += quantities[product.id] * product.price;
    });

    // Add installation if selected
    if (selectedInstallation) {
      const installation = installationOptions.find(opt => opt.id === selectedInstallation);
      if (installation) {
        total += installation.price;
      }
    }

    return total;
  };

  // Format price based on language
  const formatPrice = (price) => {
    if (language === 'en') {
      return `$${price.toFixed(2)}`;
    } else {
      return `${price.toFixed(2)} $`;
    }
  };

  // Generate WhatsApp message
  const generateWhatsAppMessage = () => {
    let message = t('whatsappGreeting') + '\n\n';

    // Add selected products
    products.forEach(product => {
      if (quantities[product.id] > 0) {
        const itemTotal = quantities[product.id] * product.price;
        message += `${quantities[product.id]}x ${t(product.nameKey)} (${formatPrice(itemTotal)})\n`;
      }
    });

    // Add installation if selected
    if (selectedInstallation) {
      const installation = installationOptions.find(opt => opt.id === selectedInstallation);
      if (installation) {
        message += `${t(installation.nameKey)} (+${formatPrice(installation.price)})\n`;
      }
    }

    message += `\n${t('whatsappTotal')} ${formatPrice(calculateTotal())}\n\n`;
    message += t('whatsappThanks');

    return encodeURIComponent(message);
  };

  // Open WhatsApp
  const openWhatsApp = () => {
    const total = calculateTotal();
    if (total === 0) {
      alert(language === 'fr' ? 'Veuillez sélectionner au moins un produit' : 
            language === 'en' ? 'Please select at least one product' :
            'Por favor selecciona al menos un producto');
      return;
    }

    const message = generateWhatsAppMessage();
    const phoneNumber = '15144643012';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pt-32 pb-20">
        <div className="container mx-auto px-4">
          {/* Language Selector */}
          <div className="flex justify-end mb-6">
            <div className="flex gap-2 bg-white rounded-full p-2 shadow-md">
              <button
                onClick={() => setLanguage('fr')}
                className={`px-4 py-2 rounded-full font-semibold transition-all ${
                  language === 'fr' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                FR
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-4 py-2 rounded-full font-semibold transition-all ${
                  language === 'en' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('es')}
                className={`px-4 py-2 rounded-full font-semibold transition-all ${
                  language === 'es' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                ES
              </button>
            </div>
          </div>

          {/* Page Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="bg-blue-100 text-blue-700 mb-4">
              <Package className="w-4 h-4 mr-2 inline" />
              {t('customizeYourKit')}
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('configuratorTitle')}
            </h1>
            <p className="text-xl text-gray-600">
              {t('configuratorSubtitle')}
            </p>
          </div>

          {/* Existing Kits */}
          <Card className="mb-12 shadow-xl border-2">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">{t('ourKits')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {kits.map((kit, index) => (
                  <div 
                    key={index}
                    className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border-2 border-blue-100 hover:border-blue-300 transition-all"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {t(kit.nameKey)}
                    </h3>
                    <p className="text-3xl font-bold text-blue-600">
                      {formatPrice(kit.price)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Product Configurator */}
            <div className="lg:col-span-2">
              <Card className="shadow-xl border-2">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900">
                    {t('customizeYourKit')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {products.map(product => (
                    <div 
                      key={product.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-2 border-gray-100 hover:border-blue-200 transition-all"
                    >
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900">{t(product.nameKey)}</h4>
                        <p className="text-lg text-blue-600 font-semibold">
                          {formatPrice(product.price)}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3 bg-white rounded-full px-3 py-2 border-2 border-gray-200">
                          <button
                            onClick={() => updateQuantity(product.id, -1)}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                          >
                            <Minus className="w-4 h-4 text-gray-700" />
                          </button>
                          <span className="text-xl font-bold text-gray-900 min-w-[2rem] text-center">
                            {quantities[product.id]}
                          </span>
                          <button
                            onClick={() => updateQuantity(product.id, 1)}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-colors"
                          >
                            <Plus className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Installation Options */}
                  <div className="pt-6 border-t-2 border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-4 text-lg">
                      {t('installationOptions')}
                    </h4>
                    <div className="space-y-3">
                      {installationOptions.map(option => (
                        <label 
                          key={option.id}
                          className="flex items-center p-4 bg-gray-50 rounded-lg border-2 cursor-pointer hover:border-blue-300 transition-all"
                          style={{
                            borderColor: selectedInstallation === option.id ? '#2563eb' : '#e5e7eb'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedInstallation === option.id}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedInstallation(option.id);
                              } else {
                                setSelectedInstallation(null);
                              }
                            }}
                            className="w-5 h-5 text-blue-600 mr-3"
                          />
                          <div className="flex-1">
                            <span className="font-semibold text-gray-900">
                              {t(option.nameKey)}
                            </span>
                          </div>
                          <span className="text-lg font-bold text-blue-600">
                            +{formatPrice(option.price)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Summary & Video */}
            <div className="space-y-6">
              {/* Total Card */}
              <Card className="shadow-xl border-2 sticky top-24">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <p className="text-sm text-gray-600 mb-2">{t('totalPrice')}</p>
                    <p className="text-5xl font-bold text-blue-600">
                      {formatPrice(calculateTotal())}
                    </p>
                  </div>

                  <Button
                    onClick={openWhatsApp}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg font-semibold"
                  >
                    <svg
                      className="w-6 h-6 mr-2"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    {t('orderViaWhatsApp')}
                  </Button>
                </CardContent>
              </Card>

              {/* Video Demo */}
              <Card className="shadow-xl border-2">
                <CardHeader>
                  <CardTitle className="text-lg text-gray-900 flex items-center">
                    <Video className="w-5 h-5 mr-2 text-blue-600" />
                    {t('videoDemo')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <video
                    controls
                    className="w-full rounded-lg shadow-lg"
                    style={{ aspectRatio: '9/16' }}
                  >
                    <source src="https://customer-assets-wrfwihn1.emergentagent.net/job_smartlogis-kit/artifacts/a022361645054c4f_video-demo-pro-smartlogis.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default KitConfigurator;
